// Schreibende Aktionen der Eigentümer-Panels (E5): Stammdaten, Status, Löschen mit Prüfung,
// Portalzugang und Zuweisung aufheben. Zuordnungen werden beendet (vertrag_bis), nie gelöscht.

import { supabase } from '../supabase/client';
import { kontoAnlegen } from '../auth/kontoAnlegen';
import { kanaeleMitPrimaer, ladeKanaele, speichereKanaele, kanaeleAusSpalten, type KontaktKanal } from '../kontakt/kontaktKanaele';
import type { Zuordnung } from './rollen';
import { aktivPatch, inaktivPatch, loeschenMoeglich, stammPayload, type EigStammEntwurf, type LoeschPruefung } from './stammdaten';

const pruefe = (r: { error: { message: string } | null }) => { if (r.error) throw new Error(r.error.message); };

// ── Stammdaten & Status ────────────────────────────────────────────────────────

export async function ladeEigKanaele(e: { id: string } & Record<string, unknown>): Promise<KontaktKanal[]> {
  const k = await ladeKanaele(supabase, 'eigentuemer', e.id);
  return k.length ? k : kanaeleAusSpalten(e);
}

export async function speichereStammdaten(id: string, e: EigStammEntwurf, start: EigStammEntwurf, kanaele: readonly KontaktKanal[]): Promise<KontaktKanal[]> {
  pruefe(await supabase.from('eigentuemer').update(stammPayload(e, start)).eq('id', id));
  const neu = kanaeleMitPrimaer(kanaele, e.email, e.telefon);
  await speichereKanaele(supabase, 'eigentuemer', id, neu);
  return neu;
}

export async function setzeEigStatus(id: string, aktiv: boolean, vertragsende: string | null | undefined, heute: string): Promise<void> {
  pruefe(await supabase.from('eigentuemer').update(aktiv ? aktivPatch(vertragsende, heute) : inaktivPatch(vertragsende, heute)).eq('id', id));
}

// ── Löschen (Prüfung wie EigentuemerLoeschenFormular) ──────────────────────────

export async function pruefeLoeschen(id: string): Promise<LoeschPruefung> {
  const [direkt, links, objektEig, einheitEig, mails] = await Promise.all([
    supabase.from('objekte').select('id').eq('eigentuemer_id', id),
    supabase.from('entity_links').select('source_id').eq('target_type', 'eigentuemer').eq('target_id', id).eq('source_type', 'objekt').eq('link_type', 'eigentum'),
    supabase.from('objekt_eigentuemer').select('objekt_id').eq('eigentuemer_id', id),
    supabase.from('einheit_eigentuemer').select('einheit_id, vertrag_bis, einheit:einheit_id(bezeichnung, objekt_id)').eq('eigentuemer_id', id),
    supabase.from('email_analysen').select('id', { count: 'exact', head: true }).eq('eigentuemer_id', id),
  ]);
  for (const r of [direkt, links, objektEig, einheitEig]) pruefe(r);
  const objektIds = new Set<string>();
  for (const r of (direkt.data ?? []) as { id: string }[]) objektIds.add(r.id);
  for (const r of (links.data ?? []) as { source_id: string }[]) objektIds.add(r.source_id);
  for (const r of (objektEig.data ?? []) as { objekt_id: string }[]) objektIds.add(r.objekt_id);
  const einheiten = (einheitEig.data ?? []) as unknown as { einheit_id: string; vertrag_bis: string | null; einheit: { bezeichnung: string | null; objekt_id: string | null } | null }[];
  for (const r of einheiten) if (r.einheit?.objekt_id) objektIds.add(r.einheit.objekt_id);
  const namen = objektIds.size
    ? ((await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').in('id', [...objektIds])).data ?? []) as { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null }[]
    : [];
  const objekte = [...objektIds].map(oid => {
    const o = namen.find(n => n.id === oid);
    const eigene = einheiten.filter(r => r.einheit?.objekt_id === oid);
    const detail = eigene.length
      ? `${eigene.map(r => (r.einheit?.bezeichnung ?? '').trim() || 'Einheit').join(', ')}${eigene.some(r => r.vertrag_bis) ? ' · inkl. beendeter Zuordnungen' : ''}`
      : 'ganzes Objekt';
    return { objektId: oid, objektName: (o?.bezeichnung ?? '').trim() || [o?.strasse, o?.hausnummer].filter(Boolean).join(' ') || 'Objekt', detail };
  });
  return { objekte, einheiten: einheiten.length, emails: mails.error ? 0 : mails.count ?? 0 };
}

/** Löscht nur, wenn keine Zuordnung mehr besteht (erneute Prüfung). Kontaktwege, Notizen und Logs gehen mit. */
export async function loescheEigentuemer(id: string): Promise<void> {
  if (!loeschenMoeglich(await pruefeLoeschen(id))) throw new Error('Der Eigentümer hat noch Zuordnungen');
  // email_analysen: FK ohne Kaskade – sonst blockiert das Löschen (wie bisher)
  pruefe(await supabase.from('email_analysen').update({ eigentuemer_id: null }).eq('eigentuemer_id', id));
  pruefe(await supabase.from('kontakt_notizen').delete().eq('quelle', 'eigentuemer').eq('quelle_id', id));
  pruefe(await supabase.from('kontakt_aktivitaeten').delete().eq('quelle', 'eigentuemer').eq('quelle_id', id));
  pruefe(await supabase.from('kontakt_kanaele').delete().eq('owner_type', 'eigentuemer').eq('owner_id', id));
  pruefe(await supabase.from('eigentuemer').delete().eq('id', id));
}

// ── Zuweisung aufheben (Eigentümer wird dort ehemalig) ─────────────────────────

/**
 * Beendet eine Zuordnung zum Stichtag `bis` (letzter Tag): Einheiten-Zeilen bzw. das ganze Objekt.
 * Die Objektverknüpfung endet mit, wenn im Objekt keine weitere laufende Zuordnung bleibt.
 */
export async function hebeZuweisungAuf(eigId: string, z: Zuordnung, alle: readonly Zuordnung[], bis: string): Promise<void> {
  const jetzt = new Date().toISOString();
  if (!z.ganzesObjekt && z.einheiten.length > 0) {
    const ids = z.einheiten.map(e => e.id);
    const { data, error } = await supabase.from('einheit_eigentuemer').select('einheit_id, vertrag_bis').eq('eigentuemer_id', eigId).in('einheit_id', ids);
    if (error) throw new Error(error.message);
    // nur offene oder später endende Zeilen – ein früheres Ende wird nie verlängert
    const offen = ((data ?? []) as { einheit_id: string; vertrag_bis: string | null }[])
      .filter(r => !r.vertrag_bis || r.vertrag_bis.slice(0, 10) > bis).map(r => r.einheit_id);
    if (offen.length) pruefe(await supabase.from('einheit_eigentuemer').update({ vertrag_bis: bis, updated_at: jetzt }).eq('eigentuemer_id', eigId).in('einheit_id', offen));
  }
  const bleibt = alle.some(x => x !== z && x.objektId === z.objektId);
  if (!bleibt) {
    const basis = () => supabase.from('objekt_eigentuemer').update({ vertrag_bis: bis }).eq('objekt_id', z.objektId).eq('eigentuemer_id', eigId);
    pruefe(await basis().is('vertrag_bis', null));
    pruefe(await basis().gt('vertrag_bis', bis));
  }
}

// ── Portalzugang (Rolle eigentuemer, wie EigentuemerPortalFormular) ────────────

/** Konto anlegen (eigener Client), profiles (Aktiv) und eigentuemer.profile_id setzen (Freigabe 13.10.2026). */
export async function gebeEigPortalFrei(p: { name: string; email: string; passwort: string; eigentuemerId: string }): Promise<void> {
  const { data, error } = await kontoAnlegen({ email: p.email, password: p.passwort, data: { full_name: p.name, role: 'eigentuemer' } });
  if (error) throw new Error(error.message);
  if (!data.user) throw new Error('Authentifizierung fehlgeschlagen.');
  pruefe(await supabase.from('profiles').upsert({ id: data.user.id, full_name: p.name, email: p.email, role: 'eigentuemer', status: 'Aktiv' }, { onConflict: 'id' }));
  const e = await supabase.from('eigentuemer').update({ profile_id: data.user.id }).eq('id', p.eigentuemerId);
  if (e.error) throw new Error(`Zugang angelegt, aber nicht mit dem Eigentümer verknüpft: ${e.error.message}`);
}

/** Sperren (Inaktiv) bzw. wieder freigeben (Aktiv) – am verknüpften Zugang (#584), sonst über E-Mail und Rolle wie bisher. */
export async function setzeEigPortalStatus(ziel: { profilId: string | null; email: string }, status: 'Aktiv' | 'Inaktiv'): Promise<void> {
  const basis = supabase.from('profiles').update({ status }).eq('role', 'eigentuemer');
  pruefe(await (ziel.profilId ? basis.eq('id', ziel.profilId) : basis.eq('email', ziel.email)));
}

/** #584: Datensatz mit einem bestehenden Eigentümer-Zugang verknüpfen (z. B. GmbH zum Zugang der Privatperson). */
export async function verknuepfeEigPortal(eigentuemerId: string, profilId: string): Promise<void> {
  const { data, error } = await supabase.from('profiles').select('role').eq('id', profilId).maybeSingle();
  if (error) throw new Error(error.message);
  if ((data as { role: string | null } | null)?.role?.toLowerCase() !== 'eigentuemer') throw new Error('Dieser Zugang ist kein Eigentümer-Zugang.');
  pruefe(await supabase.from('eigentuemer').update({ profile_id: profilId }).eq('id', eigentuemerId).is('profile_id', null));
}

/** #584: Verknüpfung lösen – der Zugang bleibt für die anderen Datensätze bestehen. */
export async function loeseEigPortal(eigentuemerId: string): Promise<void> {
  pruefe(await supabase.from('eigentuemer').update({ profile_id: null }).eq('id', eigentuemerId));
}

// ── Bank & SEPA ────────────────────────────────────────────────────────────────

export async function speichereBank(id: string, felder: Record<string, string | null>): Promise<void> {
  await aktualisiereEigentuemer(id, felder);
}

/** Einzelne Felder am Eigentümer (Vergütung, Mandatsende …). */
export async function aktualisiereEigentuemer(id: string, felder: Record<string, string | number | null>): Promise<void> {
  pruefe(await supabase.from('eigentuemer').update(felder).eq('id', id));
}

// ── Vergütung mit „gültig ab“ (eigentuemer_verguetung) ─────────────────────────

async function nutzerId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

/**
 * Neue Vergütung je Einheit ab Stichtag. Gilt sie schon heute, wird auch
 * eigentuemer.verguetung_je_einheit (heute gültiger Wert) gesetzt.
 */
export async function speichereVerguetung(eigId: string, betrag: number, ab: string, heute: string): Promise<void> {
  pruefe(await supabase.from('eigentuemer_verguetung')
    .upsert({ eigentuemer_id: eigId, betrag_je_einheit: betrag, gueltig_ab: ab, created_by: await nutzerId() }, { onConflict: 'eigentuemer_id,gueltig_ab' }));
  if (ab <= heute) {
    // nur übernehmen, wenn kein späterer, schon gültiger Eintrag existiert
    const { data, error } = await supabase.from('eigentuemer_verguetung').select('gueltig_ab').eq('eigentuemer_id', eigId).gt('gueltig_ab', ab).lte('gueltig_ab', heute).limit(1);
    if (error) throw new Error(error.message);
    if ((data ?? []).length === 0) await aktualisiereEigentuemer(eigId, { verguetung_je_einheit: betrag });
  }
}

/** Geplante (noch nicht gültige) Änderung wieder entfernen. */
export async function loescheGeplanteVerguetung(eigId: string, ab: string, heute: string): Promise<void> {
  if (ab <= heute) throw new Error('Nur geplante Änderungen können entfernt werden');
  pruefe(await supabase.from('eigentuemer_verguetung').delete().eq('eigentuemer_id', eigId).eq('gueltig_ab', ab));
}

// ── Freigaben (eigentuemer_freigaben) ──────────────────────────────────────────

export interface FreigabeEntwurf {
  objektId: string | null;
  einheitId: string | null;
  beschreibung: string;
  betrag: number | null;
  dienstleister: string;
  /** Dienstleister aus dem Dienstleister-Bereich (eigentuemer_freigaben.dienstleister_id) */
  dienstleisterId?: string | null;
}

/** Erfasst eine Freigabe-Anfrage; innerhalb der Wertgrenze gilt sie sofort als freigegeben. */
export async function erfasseFreigabe(eigId: string, f: FreigabeEntwurf, sofortFrei: boolean): Promise<void> {
  const von = await nutzerId();
  const jetzt = new Date().toISOString();
  pruefe(await supabase.from('eigentuemer_freigaben').insert({
    eigentuemer_id: eigId, objekt_id: f.objektId, einheit_id: f.einheitId, beschreibung: f.beschreibung.trim(), betrag: f.betrag,
    dienstleister_name: f.dienstleister.trim() || null, dienstleister_id: f.dienstleisterId ?? null, angefragt_von: von,
    ...(sofortFrei ? { status: 'freigegeben', entschieden_am: jetzt, entschieden_von: von, notiz: 'innerhalb der Wertgrenze – ohne Rückfrage' } : {}),
  }));
}

/** Entscheidung des Eigentümers erfassen (freigegeben / abgelehnt) oder zurück auf offen. */
export async function entscheideFreigabe(id: string, status: 'offen' | 'freigegeben' | 'abgelehnt'): Promise<void> {
  const offen = status === 'offen';
  pruefe(await supabase.from('eigentuemer_freigaben').update({
    status, entschieden_am: offen ? null : new Date().toISOString(), entschieden_von: offen ? null : await nutzerId(), updated_at: new Date().toISOString(),
  }).eq('id', id));
}

/** Felder am Objekt (WEG-Verwalterbestellung). */
export async function aktualisiereObjekt(id: string, felder: Record<string, string | null>): Promise<void> {
  pruefe(await supabase.from('objekte').update(felder).eq('id', id));
}

// ── Miteigentümer & Vertreter (eigentuemer_beteiligte), Zustellung ─────────────

export async function legeBeteiligtenAn(payload: Record<string, string | number | null>): Promise<string> {
  const { data, error } = await supabase.from('eigentuemer_beteiligte').insert({ ...payload, created_by: await nutzerId() }).select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

export async function loescheBeteiligten(id: string): Promise<void> {
  pruefe(await supabase.from('eigentuemer_beteiligte').delete().eq('id', id));
}

/** Vollmacht-Dokument an einen Vertreter hängen. */
export async function setzeBeteiligtenDokument(id: string, dokumentId: string | null): Promise<void> {
  pruefe(await supabase.from('eigentuemer_beteiligte').update({ dokument_id: dokumentId, updated_at: new Date().toISOString() }).eq('id', id));
}

/** Höchstens ein Zustellvertreter je Eigentümer (Unique-Index): erst alle lösen, dann setzen. */
export async function setzeZustellvertreter(eigId: string, beteiligterId: string | null): Promise<void> {
  const jetzt = new Date().toISOString();
  pruefe(await supabase.from('eigentuemer_beteiligte').update({ zustellvertreter: false, updated_at: jetzt }).eq('eigentuemer_id', eigId).eq('zustellvertreter', true));
  if (beteiligterId) pruefe(await supabase.from('eigentuemer_beteiligte').update({ zustellvertreter: true, updated_at: jetzt }).eq('id', beteiligterId));
}

export interface ZustellFelder {
  versandweg?: 'post' | 'email' | 'portal';
  textform_einwilligung_am?: string | null;
  post_pflicht?: Record<'etv' | 'abrechnung' | 'mahnung', boolean>;
  zustell_co?: string | null; zustell_strasse?: string | null; zustell_hausnummer?: string | null; zustell_plz?: string | null; zustell_stadt?: string | null;
}

export async function speichereZustellung(eigId: string, f: ZustellFelder): Promise<void> {
  pruefe(await supabase.from('eigentuemer').update(f).eq('id', eigId));
}
